import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Search, CheckCircle2, XCircle, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

// Parsing robusto: gestisce sia ISO sia il formato provider "2026-08-24 11:37:45 +00:00".
const parseDt = (iso) => {
  if (!iso) return null;
  let d = new Date(iso);
  if (isNaN(d.getTime())) {
    const s = String(iso).trim().replace(" ", "T").replace(/\s+([+-]\d{2}:?\d{2})$/, "$1");
    d = new Date(s);
  }
  return isNaN(d.getTime()) ? null : d;
};
const fmtRel = (iso) => {
  const d = parseDt(iso);
  if (!d) return "";
  const now = new Date(), diff = (now - d) / 1000;
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return `oggi ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })} ${time}`;
};
const fmtAbs = (iso) => {
  const d = parseDt(iso);
  if (!d) return "";
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

const KIND_LABEL = { credenziali: "Credenziali portale", pratica: "Pratica", avviso: "Avviso", sondaggio: "Sondaggio", generico: "SMS" };
const SMS_STATUS = {
  inviato: { label: "Inviato", ok: true, cls: "bg-secondary text-muted-foreground" },
  consegnato: { label: "Consegnato", ok: true, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
  errore: { label: "Errore", ok: false, cls: "bg-red-100 text-red-700" },
  non_consegnato: { label: "Non consegnato", ok: false, cls: "bg-red-100 text-red-700" },
  scaduto: { label: "Scaduto", ok: false, cls: "bg-amber-100 text-amber-700" },
};

// Storico invii SMS riutilizzabile. Con `endpoint` diverso mostra i dati globali (Super Admin)
// o quelli del solo admin corrente (Impostazioni). `showActor` mostra l'email del mittente.
export const SmsLog = ({ endpoint = "/sms/my-log", showActor = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback((query = "") => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "300" });
    if (query) params.set("q", query);
    api.get(`${endpoint}?${params.toString()}`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [endpoint]);

  useEffect(() => { load(""); }, [load]);

  const matchesFilter = (e) => {
    if (filter === "all") return true;
    if (filter === "consegnato") return e.status === "consegnato";
    if (filter === "attesa") return e.status === "inviato";
    if (filter === "non_consegnato") return ["non_consegnato", "scaduto", "errore"].includes(e.status);
    return true;
  };
  const shown = events.filter(matchesFilter);
  const counts = {
    all: events.length,
    consegnato: events.filter((e) => e.status === "consegnato").length,
    attesa: events.filter((e) => e.status === "inviato").length,
    non_consegnato: events.filter((e) => ["non_consegnato", "scaduto", "errore"].includes(e.status)).length,
  };
  const FILTERS = [
    { id: "all", label: "Tutti" },
    { id: "consegnato", label: "Consegnati" },
    { id: "attesa", label: "In attesa" },
    { id: "non_consegnato", label: "Non consegnati" },
  ];

  return (
    <div data-testid="sms-log-card">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(q); }}
            placeholder="Cerca per numero, nome, condominio o tipo…" className="pl-9 h-10" data-testid="sms-log-search" />
        </div>
        <button onClick={() => load(q)} data-testid="sms-log-refresh"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Aggiorna
        </button>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="sms-log-filters">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`sms-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${filter === f.id
              ? "bg-[#007BFF] text-white border-[#007BFF]"
              : "bg-background text-muted-foreground border-border hover:border-[#007BFF]/50"}`}>
            {f.label}
            <span className={`text-[10px] px-1.5 rounded-full ${filter === f.id ? "bg-white/25" : "bg-secondary"}`}>{counts[f.id]}</span>
          </button>
        ))}
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="sms-log-list">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-16">{events.length === 0 ? "Nessun SMS inviato finora." : "Nessun SMS per questo filtro."}</div>
        ) : (
          <div className="divide-y divide-border/60 max-h-[520px] overflow-y-auto scroll-thin" data-testid="sms-log-scroll">
            {shown.map((e) => {
              const st = SMS_STATUS[e.status] || SMS_STATUS.inviato;
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} className="px-4 py-3 text-sm hover:bg-secondary/40 list-enter" data-testid={`sms-row-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {st.ok ? <CheckCircle2 className={`h-4 w-4 ${e.status === "consegnato" ? "text-emerald-500" : "text-sky-500"}`} /> : <XCircle className="h-4 w-4 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium font-mono">{e.recipient}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">{KIND_LABEL[e.kind] || e.kind}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}{e.status === "inviato" && e.segments ? ` · ${e.segments} SMS` : ""}</span>
                        {e.delivered_at && fmtAbs(e.delivered_at) && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid={`sms-delivered-${e.id}`}>Consegnato il {fmtAbs(e.delivered_at)}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {e.building_name || "—"}{e.recipient_name ? <> · <span className="font-medium text-foreground/70" data-testid={`sms-name-${e.id}`}>{e.recipient_name}</span></> : ""}{showActor && e.actor_email ? ` · ${e.actor_email}` : ""}{e.error ? ` · ${e.error}` : ""}
                      </div>
                      {e.body && (
                        <button data-testid={`sms-toggle-${e.id}`} onClick={() => setExpanded(isOpen ? null : e.id)}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#007BFF] hover:underline">
                          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {isOpen ? "Nascondi messaggio" : "Mostra messaggio"}
                        </button>
                      )}
                      {e.body && isOpen && (
                        <div data-testid={`sms-body-${e.id}`} className="mt-1.5 text-[12px] text-foreground/80 bg-secondary/60 border border-border rounded-md px-2.5 py-2 whitespace-pre-wrap break-words">
                          {e.body}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtRel(e.ts)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
